import type { PaletteOptions } from '@mui/material/styles';

/**
 * OTA brand palette — MakeMyTrip inspired.
 *
 * Primary deep blue is used for headers, primary CTAs, and active states.
 * Secondary saffron-orange is the booking accent (CTAs that drive conversion:
 * "Book Now", "Search", "Apply Coupon"). Tertiary mustard for highlights
 * (deals, offers, "Limited time" tags).
 */
export const otaColors = {
  // Primary brand — deep blue
  primary: {
    50: '#E6EEF7',
    100: '#C0D4E9',
    200: '#97B7DA',
    300: '#6F9ACA',
    400: '#5384BE',
    500: '#053C71', // brand primary
    600: '#04356A',
    700: '#032D60',
    800: '#022454',
    900: '#01173E',
  },
  // Secondary — booking-conversion saffron
  secondary: {
    50: '#FFF1E0',
    100: '#FFD9B3',
    200: '#FFC080',
    300: '#FFA64D',
    400: '#FF9326',
    500: '#EB6F03', // brand secondary
    600: '#D86603',
    700: '#B35402',
    800: '#8E4202',
    900: '#5C2A01',
  },
  // Tertiary — deal mustard
  accent: {
    main: '#F2A93B',
    light: '#FFD37A',
    dark: '#C68312',
  },
  // Semantic
  success: { main: '#00A859', light: '#5FD3A0', dark: '#007B41' },
  warning: { main: '#F2A93B', light: '#FFD37A', dark: '#B07814' },
  error: { main: '#E74C3C', light: '#F39189', dark: '#A52A1F' },
  info: { main: '#1F8DD6', light: '#79BCE5', dark: '#0E5F95' },
  // Neutrals
  text: {
    primary: '#1A1A1A',
    secondary: '#4A4A4A',
    disabled: '#9AA0A6',
    inverse: '#FFFFFF',
  },
  background: {
    default: '#F5F7FA',
    paper: '#FFFFFF',
    subtle: '#F0F3F7',
    dark: '#0B2A4F',
  },
  border: {
    default: '#E0E4EA',
    strong: '#C8CFD8',
  },
} as const;

export const palette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: otaColors.primary[500],
    light: otaColors.primary[300],
    dark: otaColors.primary[700],
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: otaColors.secondary[500],
    light: otaColors.secondary[300],
    dark: otaColors.secondary[700],
    contrastText: '#FFFFFF',
  },
  success: otaColors.success,
  warning: otaColors.warning,
  error: otaColors.error,
  info: otaColors.info,
  text: {
    primary: otaColors.text.primary,
    secondary: otaColors.text.secondary,
    disabled: otaColors.text.disabled,
  },
  background: {
    default: otaColors.background.default,
    paper: otaColors.background.paper,
  },
  divider: otaColors.border.default,
};
